import { useEffect, useState } from "react";
import {
  CheckCircle2Icon,
  Loader2Icon,
  AlertTriangleIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { cn } from "@util/tailwind";

const STEPS = [
  { key: "transcribing", label: "Transcribing your roleplay" },
  { key: "scoring", label: "Scoring against rubric" },
  { key: "feedback", label: "Generating feedback" },
] as const;

export type AnalyzingStatus = "running" | "timeout" | "failed";

export function TrainingAnalyzingCard({
  startedAt,
  status,
  failedReason,
  onRetry,
}: {
  startedAt: number;
  status: AnalyzingStatus;
  failedReason?: string | null;
  onRetry?: () => void;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (status !== "running") return;
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [status]);

  const elapsed = Math.max(0, Date.now() - startedAt);
  const stepIdx = Math.min(STEPS.length - 1, Math.floor(elapsed / 8000));

  if (status === "timeout") {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 text-amber-600 mb-4">
            <AlertTriangleIcon className="w-6 h-6" />
          </div>
          <div className="text-xl font-semibold text-gray-900 mb-2">
            This is taking longer than usual.
          </div>
          <div className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
            Your roleplay was saved. We'll email you the results as soon as
            they're ready, or you can try again now.
          </div>
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              Check again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (status === "failed") {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-600 mb-4">
            <AlertTriangleIcon className="w-6 h-6" />
          </div>
          <div className="text-xl font-semibold text-gray-900 mb-2">
            We couldn't score this roleplay.
          </div>
          <div className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
            Your recording was saved and the error has been reported. Please
            try the roleplay again, or contact your HR team if this keeps
            happening.
          </div>
          {failedReason && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-6 max-w-md mx-auto text-left">
              <span className="font-medium text-gray-700">Reason:</span>{" "}
              {failedReason}
            </div>
          )}
          {onRetry && <Button onClick={onRetry}>Try again</Button>}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-10">
        <div className="text-center mb-8">
          <div className="text-xl font-semibold text-gray-900 mb-2">
            Analysing your performance.
          </div>
          <div className="text-sm text-gray-600">
            This usually takes under 60 seconds.
          </div>
        </div>

        <ol className="space-y-3 max-w-sm mx-auto">
          {STEPS.map((step, i) => {
            const state =
              i < stepIdx ? "done" : i === stepIdx ? "active" : "pending";
            return (
              <li
                key={step.key}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors",
                  state === "active" && "bg-blue-50",
                  state === "done" && "text-emerald-700",
                  state === "pending" && "text-gray-400"
                )}
              >
                <div className="flex-shrink-0">
                  {state === "done" ? (
                    <CheckCircle2Icon className="w-5 h-5 text-emerald-500" />
                  ) : state === "active" ? (
                    <Loader2Icon className="w-5 h-5 text-blue-600 animate-spin" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
                  )}
                </div>
                <div
                  className={cn(
                    "text-sm",
                    state === "active" && "font-medium text-blue-900",
                    state === "done" && "text-gray-700",
                    state === "pending" && "text-gray-400"
                  )}
                >
                  {step.label}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-8 flex justify-center">
          <div className="h-1 w-64 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{
                width: `${Math.min(95, (elapsed / 90000) * 100)}%`,
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
